import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/base/ComicCard";
import { comics } from "@/mocks/comics";
import usePageMeta, { SITE_NAME } from "@/hooks/usePageMeta";

const hotKeywords = ["爱情", "奇幻", "校园", "悬疑", "治愈", "甜宠", "热血"];

function matchComic(keyword: string) {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return comics.filter((comic) => {
    const haystack = [
      comic.title,
      comic.subTitle,
      comic.author,
      ...comic.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [showSuggest, setShowSuggest] = useState(false);

  usePageMeta(
    query ? `搜索“${query}” - ${SITE_NAME}` : `搜索漫画 - ${SITE_NAME}`,
    query
      ? `在韩漫大全搜索“${query}”相关的韩漫作品，免费无删减在线阅读。`
      : "韩漫大全搜索页，输入关键词快速查找你想看的韩漫作品，支持标题与标签搜索。",
  );

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => matchComic(query), [query]);

  const suggestions = useMemo(() => {
    if (!input.trim()) return [];
    return matchComic(input).slice(0, 5);
  }, [input]);

  const hotComics = useMemo(
    () => [...comics].sort((a, b) => b.views - a.views).slice(0, 4),
    [],
  );

  const submit = (value: string) => {
    const kw = value.trim();
    setShowSuggest(false);
    if (!kw) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 text-center">
            搜索韩漫
          </h1>
          <p className="text-sm text-foreground-500 text-center mt-2">
            输入漫画名称、作者或标签，快速找到你想看的内容
          </p>

          <div className="relative mt-6">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(input);
              }}
              className="flex items-center gap-2"
            >
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  type="text"
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="例如：爱情、校园、心动信号…"
                  inputMode="search"
                  className="w-full h-12 md:h-14 pl-11 pr-4 rounded-md border border-background-300 bg-background-100 text-sm md:text-base text-foreground-900 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200 transition"
                />
              </div>
              <button
                type="submit"
                className="h-12 md:h-14 px-6 md:px-8 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            {showSuggest && suggestions.length > 0 && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowSuggest(false)}
                ></div>
                <ul className="absolute left-0 right-0 top-full mt-2 z-40 rounded-lg bg-background-50 border border-background-200 overflow-hidden shadow-sm">
                  {suggestions.map((comic) => (
                    <li key={comic.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSuggest(false);
                          navigate(`/detail/${comic.id}`);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background-100 transition-colors text-left cursor-pointer"
                      >
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          className="w-10 h-14 rounded object-cover object-top shrink-0"
                        />
                        <span className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-foreground-950 truncate">
                            {comic.title}
                          </span>
                          <span className="text-xs text-foreground-500 truncate">
                            {comic.tags.join(" · ")}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => submit(word)}
                className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-700 border border-background-200 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12">
          {query ? (
            <>
              <p className="text-sm text-foreground-600 mb-5">
                关键词 <span className="font-semibold text-foreground-950">“{query}”</span>{" "}
                共找到{" "}
                <span className="font-semibold text-accent-600">
                  {results.length}
                </span>{" "}
                部漫画
              </p>
              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {results.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-16 px-4 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-4">
                    <i className="ri-search-eye-line"></i>
                  </span>
                  <h2 className="font-heading font-semibold text-lg text-foreground-950">
                    没有找到“{query}”相关的漫画
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2 max-w-sm">
                    换个关键词试试，或者看看下面这些热门作品吧。
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-foreground-500 text-center mb-5">
              输入关键词开始搜索，下面是当前热门推荐
            </p>
          )}

          {(query ? results.length === 0 : true) && (
            <section className="mt-10">
              <div className="flex items-center gap-2 mb-5">
                <i className="ri-fire-line text-accent-500 text-xl"></i>
                <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950">
                  热门漫画推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {hotComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              <div className="flex justify-center mt-8">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
                >
                  浏览全部漫画
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}